import {
	Button,
	Drawer,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	DrawerHeader,
	DrawerHeading,
	DrawerProps,
	I18nFormProvider,
	I18nLocaleSwitcherBase,
	Icon,
	Id,
	Trigger,
	useEditDrawer,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { contactAttributeKeys } from "../shared/api/contactAttributeService";
import { contactFilterService } from "../shared/api/contactFilterService";
import { createDataFieldOptionService } from "../shared/api/dataFieldService";
import { DataFieldOptionCreateDrawer, DataFieldOptionDrawer } from "./DataFieldOptionDrawer";
import { DataFieldOptionSortableList } from "./DataFieldOptionSortableList";

interface Props extends Omit<DrawerProps, "children"> {
	dataFieldId: Id;
	initialLocale?: string;
}

export const DataFieldOptionListDrawer: FC<Props> = ({ dataFieldId, initialLocale, ...overlayDrawerProps }) => {
	const { t } = useTranslation();
	const dataFieldOptionService = useMemo(() => createDataFieldOptionService(dataFieldId.toString()), [dataFieldId]);
	const crudOperations = useLaravelCrudEntity(
		dataFieldOptionService,
		{
			entityName: t("Data field option"),
			messages: {
				create: t("Data field option was saved."),
				update: t("Data field option was saved."),
				delete: t("Data field option was deleted."),
			},
		},
		[dataFieldOptionService.indexKey, contactAttributeKeys.index, contactFilterService.indexKey],
	);

	const editOptionDrawerProps = useEditDrawer();

	return (
		<Drawer {...overlayDrawerProps}>
			<I18nFormProvider initialLanguage={initialLocale}>
				<DrawerHeader onClose={overlayDrawerProps.onClose}>
					<DrawerHeading>{t("Data field options")}</DrawerHeading>
					<I18nLocaleSwitcherBase onChange={() => void 0} />
				</DrawerHeader>
				<DrawerBody>
					<DataFieldOptionSortableList
						dataFieldId={dataFieldId}
						onEditClick={editOptionDrawerProps.handleOpen}
						onDeleteClick={crudOperations.deleteEntity}
					/>
				</DrawerBody>
				<DrawerFooter>
					<DrawerFooterActions />
					<Trigger
						render={(props) => (
							<DataFieldOptionCreateDrawer
								open={props.open}
								onClose={props.handleClose}
								onClosed={() => void 0}
								onSubmit={crudOperations.createEntity}
							/>
						)}
					>
						{(props) => (
							<Button {...props} type="button" color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add data field option")}
							</Button>
						)}
					</Trigger>
				</DrawerFooter>
				{editOptionDrawerProps.editEntityId && (
					<DataFieldOptionDrawer
						{...editOptionDrawerProps}
						id={editOptionDrawerProps.editEntityId}
						onClosed={() => {
							editOptionDrawerProps.onClosed();
						}}
						onSubmit={(formState) => crudOperations.updateEntity(editOptionDrawerProps.editEntityId as Id, formState)}
						queryKey={dataFieldOptionService.showKey(editOptionDrawerProps.editEntityId as Id)}
						queryFn={() =>
							dataFieldOptionService.show(editOptionDrawerProps.editEntityId as Id).then((res) => res.data.data)
						}
					/>
				)}
			</I18nFormProvider>
		</Drawer>
	);
};
